import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { ITableConfigProp } from "components/dashboard/data-input-output-table/common/api/interfaces/interfaces";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React from "react";
import { T } from "services/lang/LanguageService";
import { handleRowReorderEvt, rangeBody } from "utils/utils";

const TYPE_OPTIONS = [{ value: "NUMBER" }, { value: "BOOLEAN" }, { value: "ENUM" }];

interface DatasetConfigEditorProps {
  currentConfig: ITableConfigProp;
  datasetConfigEditorIsVisible: boolean;
  setCurrentConfig: React.Dispatch<React.SetStateAction<ITableConfigProp>>;
  setDatasetConfigEditorVisibility: (datasetConfigEditorisVisible: boolean) => void;
  onConfigChange: (config: ITableConfigProp) => void;
  setSelectedDatasets: (arr: string[]) => void;
}

const DatasetConfigEditor = ({
  currentConfig,
  datasetConfigEditorIsVisible,
  setCurrentConfig,
  setDatasetConfigEditorVisibility,
  onConfigChange,
  setSelectedDatasets,
}: DatasetConfigEditorProps) => {
  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const datasetsToUpdate = [...currentConfig.datasets];
    const { newData, index } = e;

    datasetsToUpdate[index] = newData;
    setCurrentConfig({ ...currentConfig, datasets: datasetsToUpdate });
    onConfigChange({ ...currentConfig, datasets: datasetsToUpdate });
  };

  const textEditor = (options: ColumnEditorOptions) => {
    if (
      options.field === "description" &&
      currentConfig.datasets[options.rowIndex].identifier === ""
    ) {
      return null;
    }
    return (
      <InputText
        value={options.value ?? ""}
        onChange={e => options.editorCallback?.(e.target.value)}
        style={{ width: "100%" }}
      />
    );
  };

  const numberEditor = (options: ColumnEditorOptions) => {
    if (currentConfig.datasets[options.rowIndex].identifier !== "") {
      return (
        <InputNumber
          value={options.value}
          onChange={e => options.editorCallback?.(e.value)}
          inputStyle={{ width: "100%" }}
        />
      );
    }
    return null;
  };

  const handleRangeEditorCallback = (options: ColumnEditorOptions, newRange: Array<number>) => {
    if (!currentConfig.datasets) {
      return;
    }
    const { identifier } = options.rowData;
    const datasetIndex = currentConfig.datasets.findIndex(
      dataset => dataset.identifier === identifier
    );
    if (datasetIndex !== -1) {
      const updatedDatasets = [...currentConfig.datasets];
      updatedDatasets[datasetIndex] = { ...updatedDatasets[datasetIndex], valueRange: newRange };
      setCurrentConfig({ ...currentConfig, datasets: updatedDatasets });
      options.editorCallback!(newRange);
    }
  };

  const handleNumberRange = (options: ColumnEditorOptions) => {
    const currentRange = options.rowData.valueRange || [undefined, undefined];

    if (
      options.rowData.type?.toUpperCase() === "BOOLEAN" ||
      options.rowData.type?.toUpperCase() === "ENUM" ||
      (options.rowData.type === undefined && options.rowData.identifier === "")
    ) {
      return null;
    }
    return (
      <div className="flex flex-row gap-2">
        <InputNumber
          inputStyle={{ width: "100%", height: "100%" }}
          value={currentRange[0]}
          maxFractionDigits={2}
          placeholder="MIN"
          onValueChange={e => {
            const newRange = [e.value, currentRange[1]];
            handleRangeEditorCallback(options, newRange);
          }}
        />
        <InputNumber
          inputStyle={{ width: "100%", height: "100%" }}
          maxFractionDigits={2}
          value={currentRange[1]}
          placeholder="MAX"
          onValueChange={e => {
            const newRange = [currentRange[0], e.value];
            handleRangeEditorCallback(options, newRange);
          }}
        />
      </div>
    );
  };

  const typeEditor = (options: ColumnEditorOptions) => {
    if (currentConfig.datasets[options.rowIndex].identifier !== "") {
      const handleTypeChange = e => {
        const datasetsToUpdate = [...currentConfig.datasets];
        datasetsToUpdate[options.rowIndex] = {
          ...datasetsToUpdate[options.rowIndex],
          type: e.target.value,
        };

        if (e.target.value.toUpperCase() !== "ENUM") {
          delete datasetsToUpdate[options.rowIndex].enum;
        }

        setCurrentConfig({ ...currentConfig, datasets: datasetsToUpdate });
        onConfigChange({ ...currentConfig, datasets: datasetsToUpdate });
        options.editorCallback?.(e.target.value);
      };

      return (
        <Dropdown
          value={options.value}
          onChange={handleTypeChange}
          options={TYPE_OPTIONS}
          optionLabel="value"
          placeholder={T("action.select-type")}
          style={{ width: "100%" }}
        />
      );
    }
    return null;
  };

  const enumEditor = (options: ColumnEditorOptions) => {
    if (
      currentConfig.datasets[options.rowIndex].type?.toUpperCase() === "ENUM" &&
      currentConfig.datasets[options.rowIndex].identifier !== ""
    ) {
      const enumArray = options.value
        ? Object.entries(options.value).map(([key, value]) => `${key}:${value}`)
        : [];

      const handleEnumChange = e => {
        // Validate the entered values
        const isValid = e.value.every(item => {
          const [key, value] = item.split(":");
          return key && !isNaN(Number(value));
        });

        if (isValid) {
          const enumObject = e.value.reduce((obj, item) => {
            const [key, value] = item.split(":");
            obj[key] = Number(value);
            return obj;
          }, {});

          options.editorCallback?.(enumObject);
        } else {
          easasAlert({
            summary: T("alert.invalid-enum"),
            detail: T("alert.please-enter-key-value-pairs"),
            severity: "warn",
          });
        }
      };

      return <Chips value={enumArray} onChange={handleEnumChange} placeholder="text:value" />;
    }
    return null;
  };

  const rangeBodyTemplate = (rowData: any) => {
    if (
      rowData.type?.toUpperCase() === "NUMBER" ||
      (rowData.type === undefined && rowData.identifier !== "")
    ) {
      const currentRange =
        currentConfig.datasets &&
        currentConfig.datasets.find(dataset => dataset.identifier === rowData.identifier)
          ?.valueRange;

      return <div>{rangeBody(currentRange)}</div>;
    }
    return null;
  };

  const enumBodyTemplate = rowData => {
    if (rowData.type?.toUpperCase() === "ENUM") {
      return rowData.enum
        ? Object.entries(rowData.enum).map(([key, value]) => {
            return (
              <div key={key}>
                {key}: {value}
              </div>
            );
          })
        : "No enum";
    }
    return null;
  };

  const titleBodyTemplate = (rowData: any) => {
    return rowData.identifier && rowData.identifier.length > 0 ? (
      <DatasetTitle identifier={rowData.identifier} />
    ) : (
      <div>{T("word.separator")}</div>
    );
  };

  const handleAddSeparator = () => {
    const tempConfig = structuredClone(currentConfig);
    const newValue = { identifier: "" };
    tempConfig.datasets.push(newValue);
    setCurrentConfig(tempConfig);
    setSelectedDatasets(tempConfig.datasets.map(dataset => dataset.identifier));
  };

  const handleDialogHeader = () => {
    return (
      <div className="flex flex-row justify-content-between pr-2">
        <div>{T("action.edit-dataset-config")}</div>
        <Button
          label={T("action.add-separator")}
          icon="pi pi-plus"
          className="p-button-info"
          onClick={handleAddSeparator}
        />
      </div>
    );
  };
  const editableBodyTemplate = (rowData: any) => {
    return rowData.identifier.length > 0 ? (
      <InputSwitch checked={rowData.editable !== false} disabled />
    ) : null;
  };

  const editableEditor = (options: any) => {
    if (currentConfig.datasets[options.rowIndex].identifier !== "") {
      return (
        <InputSwitch
          checked={options.value ?? true}
          onChange={e => options.editorCallback(e.target.value)}
        />
      );
    }
    return null;
  };

  return (
    <Dialog
      onHide={() => setDatasetConfigEditorVisibility(false)}
      visible={datasetConfigEditorIsVisible}
      header={handleDialogHeader}
      style={{ width: "min(100vw, 1500px)", height: "500x" }}
    >
      <DataTable
        value={currentConfig.datasets}
        editMode="row"
        onRowEditComplete={onRowEditComplete}
        tableStyle={{ width: "100%" }}
        onRowReorder={handleRowReorderEvt(
          currentConfig.datasets,
          setCurrentConfig,
          currentConfig,
          "datasets"
        )}
        reorderableRows
      >
        <Column rowReorder style={{ width: "3%" }} />
        <Column
          className="p-0"
          field="identifier"
          header={T("word.title")}
          style={{ width: "25%" }}
          align="center"
          body={titleBodyTemplate}
        />
        <Column
          className="p-0"
          field="decimalPlaces"
          header={T("word.decimal")}
          style={{ width: "5%" }}
          align="center"
          editor={options => numberEditor(options)}
        />
        <Column
          className="p-0"
          field="overriddenTitle"
          header={T("word.overridden-title")}
          style={{ width: "12%" }}
          align="center"
          editor={options => textEditor(options)}
        />
        <Column
          className="p-0"
          field="description"
          header={T("word.description")}
          style={{ width: "25%" }}
          align="center"
          editor={options => textEditor(options)}
        />
        <Column
          className="p-0"
          field="type"
          header={T("word.type")}
          style={{ width: "10%" }}
          align="center"
          editor={options => typeEditor(options)}
        />
        <Column
          className="p-0"
          field="enum"
          header="Enum"
          style={{ width: "10%" }}
          align="center"
          editor={options => enumEditor(options)}
          body={enumBodyTemplate}
        />
        <Column
          className="p-0"
          field="editable"
          header={T("word.editable")}
          style={{ width: "10%" }}
          align="center"
          body={editableBodyTemplate}
          editor={options => editableEditor(options)}
        />
        <Column
          className="p-0"
          field="valueRange"
          header={T("word.range")}
          style={{ width: "10%" }}
          align="center"
          body={rangeBodyTemplate}
          editor={options => handleNumberRange(options)}
        />
        <Column
          className="p-0"
          rowEditor
          style={{ width: "10%" }}
          headerStyle={{ width: "10%" }}
          bodyStyle={{ textAlign: "center" }}
        />
      </DataTable>
    </Dialog>
  );
};

export default DatasetConfigEditor;
